import fp from 'lodash/fp';
import React, { useRef, useMemo, useCallback, MouseEventHandler } from 'react';
import { VariableSizeGrid, GridChildComponentProps } from 'react-window';

import { Checkbox, multipleRef, cn, useRemUpdatedCallback, FactorinCheckClass, useMemoDeps } from 'Modules/ui';


import { GridAdapter } from '../GridAdapter';
import { cellStyles } from '../Cells';
import { BODY_CELL_CLASSNAME } from '../constants';
import {
    CheckboxItemProps,
    CheckableColumnComponentProps,
    CheckboxComponentProps,
    CheckboxProps,
} from './types';
import styles from './index.module.scss';


const checkModel = new FactorinCheckClass<number>();

const CheckboxComponent = React.memo<CheckboxComponentProps>(({
    component: Component = Checkbox,
    disabled,
    rowIndex,
    data,
    checkedKeySelector,
    checkedRows,
    onCheckedChanged,
}) => {
    const checkedKey = useMemoDeps(checkedKeySelector, [data, rowIndex]);
    const checked = useMemoDeps(checkModel.getSingle, [checkedKey, checkedRows]);
    const onChange = useCallback<CheckboxProps['onChange']>(
        ({ target }) => onCheckedChanged(checkModel.updateSingle(
            checkedKey,
            checkedRows,
            target.checked,
        )),
        [checkedKey, checkedRows, onCheckedChanged],
    );

    return (
        <Component
            disabled={disabled}
            className={styles.checkbox}
            markClassName={styles.checkboxMark}
            checked={checked}
            onChange={onChange}
        />
    );
});

const checkBoxColumnRenderer = React.memo<GridChildComponentProps<CheckboxItemProps>>(({
    rowIndex,
    style,
    data: {
        checkable,
        disabled,
        selectedRow,
        className,
        setHoveredRowIndex,
        checkedRows,
        onCheckedChanged,
        checkedKeySelector,
        container,
        checkbox,
        getData,
    },
}) => {
    const data = useMemo(
        () => getData(rowIndex),
        [getData, rowIndex],
    );

    const Container = container || 'div';

    return (
        <Container
            style={style as React.CSSProperties}
            className={cn(
                cellStyles.cell,
                styles.checkboxCell,
                BODY_CELL_CLASSNAME,
                fp.isFunction(className) ? className(data) : className,
                selectedRow === rowIndex && styles.selected,
            )}
            data={data}
            onMouseEnter={useCallback(
                () => setHoveredRowIndex(rowIndex),
                [rowIndex, setHoveredRowIndex],
            )}
            onMouseLeave={useCallback<MouseEventHandler>(
                ({ relatedTarget }) => {
                    const element = relatedTarget as Element;
                    return !element.classList || element.classList.contains(BODY_CELL_CLASSNAME)
                        ? null
                        : setHoveredRowIndex(null);
                },
                [setHoveredRowIndex],
            )}
        >
            {(!fp.isFunction(checkable) || checkable(data, rowIndex)) && (
                <CheckboxComponent
                    component={checkbox}
                    rowIndex={rowIndex}
                    data={data}
                    disabled={disabled}
                    checkedKeySelector={checkedKeySelector}
                    checkedRows={checkedRows}
                    onCheckedChanged={onCheckedChanged}
                />
            )}
        </Container>
    );
});

const CheckableColumnComponent = React.memo<CheckableColumnComponentProps>(({
    width,
    checkable,
    disabled,
    selectedRow,
    className,
    checkedRows,
    checkedKeySelector = (_, index) => index,
    onCheckedChanged,
    setHoveredRowIndex,
    getData,
    checkbox,
    checkboxContainer,
    coreRef,
    ...props
}) => {
    const checkboxColumnRef = useRef<VariableSizeGrid>();
    useRemUpdatedCallback(() => {
        if (checkboxColumnRef.current)
            checkboxColumnRef.current.resetAfterIndices({ rowIndex: 0, columnIndex: 0 });
    });

    return (
        <GridAdapter
            ref={multipleRef(checkboxColumnRef, coreRef)}
            className={styles.container}
            width={width}
            columnCount={1}
            columnWidth={fp.constant(width)}
            {...props}
            itemData={useMemo<CheckboxItemProps>(
                () => ({
                    checkable,
                    disabled,
                    selectedRow,
                    className,
                    setHoveredRowIndex,
                    checkedRows,
                    checkedKeySelector,
                    onCheckedChanged,
                    getData,
                    container: checkboxContainer,
                    checkbox,
                }),
                [
                    checkable,
                    disabled,
                    selectedRow,
                    className,
                    checkedRows,
                    setHoveredRowIndex,
                    onCheckedChanged,
                    checkedKeySelector,
                    getData,
                    checkbox,
                    checkboxContainer,
                ],
            )}
        >
            {checkBoxColumnRenderer}
        </GridAdapter>
    );
});

export type CheckableColumnProps = Omit<CheckableColumnComponentProps, 'coreRef'>;

export const CheckableColumn = React.forwardRef<VariableSizeGrid, CheckableColumnProps>(
    (props, ref) => <CheckableColumnComponent coreRef={ref} {...props} />,
);
